import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import AnimatedBackground from './components/AnimatedBackground';
import MediaShowcase from './components/MediaShowcase';
import LatestGamesSection from './components/home/LatestGamesSection';
import LatestBlogSection from './components/home/LatestBlogSection';
import FeaturedCommentsSection from './components/home/FeaturedCommentsSection';
import { Games } from './pages/Games';
import { GameDetail } from './pages/GameDetail';
import NotFound from './pages/NotFound';
import { AuthProvider } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { RegionProvider } from './contexts/RegionContext';
import ProtectedRoute from './components/ProtectedRoute';
import { MyGames } from './pages/MyGames';
import TermsAndConditionsPage from './pages/TermsAndConditions';
import TermsManagement from './pages/admin/TermsManagement';
import './styles/admin.css';
import { Footer } from './components/Footer';
import DownloadPage from './pages/Download';
import DownloadLinksAdmin from './pages/admin/DownloadLinksAdmin';
import Contact from './pages/Contact';
import About from './pages/About';
import TeamAdmin from './pages/TeamAdmin';
import GameEngine from './pages/GameEngine';
import { HelmetProvider } from 'react-helmet-async';
import BlogPage from './pages/Blog';
import BlogPostPage from './pages/BlogPost';
import BlogAdminPage from './pages/admin/BlogAdmin';
import AIAgentsAdminPage from './pages/admin/AIAgentsAdmin';
import BlogCommentsAdminPage from './pages/admin/BlogCommentsAdmin';
import DefaultAvatarsAdminPage from './pages/admin/DefaultAvatarsAdmin';
import YektanetAdminPage from './pages/admin/YektanetAdmin';
import ProfilePage from './pages/Profile';
import CommentsAdminPage from './pages/admin/CommentsAdmin';
import UsersAdminPage from './pages/admin/UsersAdmin';
import RolesAdminPage from './pages/admin/RolesAdmin';
import ContactLinksAdminPage from './pages/admin/ContactLinksAdmin';
import GrowthIntelligenceAdminPage from './pages/admin/GrowthIntelligenceAdmin';
import AuthCallbackPage from './pages/auth/callback';

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <ProfileProvider>
            <RegionProvider>
              <div className="min-h-screen flex flex-col relative bg-black text-white">
                <AnimatedBackground />
                <div className="relative z-20 flex flex-col flex-1 min-h-screen">
                  <Navbar />
                  <main className="flex-1 flex flex-col">
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <>
                            <Hero />
                            <MediaShowcase />
                            <LatestGamesSection />
                            <LatestBlogSection />
                            <FeaturedCommentsSection />
                          </>
                        }
                      />
                      <Route path="/games" element={<Games />} />
                      <Route path="/games/:slug" element={<GameDetail />} />
                      <Route
                        path="/my-games"
                        element={
                          <ProtectedRoute>
                            <MyGames />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/terms" element={<TermsAndConditionsPage />} />
                      <Route
                        path="/admin/terms"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_terms">
                            <TermsManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/download-links"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_downloads">
                            <DownloadLinksAdmin />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/download/:id" element={<DownloadPage />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/game" element={<GameEngine />} />
                      <Route
                        path="/team-admin"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_team">
                            <TeamAdmin />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      <Route
                        path="/admin/ai-agents"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_ai">
                            <AIAgentsAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/roles"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_roles">
                            <RolesAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/growth"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_ai">
                            <GrowthIntelligenceAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/contact-links"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_contacts">
                            <ContactLinksAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/blog"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_blog">
                            <BlogAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/blog-comments"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_comments">
                            <BlogCommentsAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/default-avatars"
                        element={
                          <ProtectedRoute adminOnly>
                            <DefaultAvatarsAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/yektanet"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_yektanet">
                            <YektanetAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <ProfilePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_users">
                            <UsersAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/comments"
                        element={
                          <ProtectedRoute adminOnly requiredPermission="can_manage_comments">
                            <CommentsAdminPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/auth/callback" element={<AuthCallbackPage />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
              </div>
            </RegionProvider>
          </ProfileProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
